import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query"
import type { MessageType } from "@univdiam/ui/type"
import {
  mergeChatMessages,
  upsertMessage,
  upsertMessages,
  newClientMessageId,
} from "@univdiam/ui/lib/chatMessages"
import { isOppositeSide } from "@univdiam/ui/lib/chatSides"

/**
 * All of the delivery machinery for one order's chat, in one place.
 *
 * The admin and customer portals render different chrome around the same
 * conversation, and previously each owned its own copy of this logic. That
 * duplication is what let the original disappearing-message bug survive a fix:
 * the two copies drifted, and only one of them was ever corrected. The pure
 * reconciliation helpers live in lib/chatMessages; this hook owns the wiring
 * that connects them to react-query and the socket, so there is exactly one of
 * each.
 *
 * Everything app-specific is injected: the HTTP client and the socket store are
 * per-app modules, so the hook takes functions rather than importing them.
 */

/** Shape of the acknowledgement the server sends back for a send. */
export interface SendAckResult {
  ok: boolean
  message?: MessageType
  code?: string
}

export interface UseOrderChatParams {
  /**
   * orders.id -- the numeric primary key. Used for the REST path and as the
   * react-query key.
   */
  orderDbId?: string | number
  /**
   * orders.order_id -- the human reference (UD####). This is the socket room
   * key and what incoming messages are filtered against. NOT interchangeable
   * with orderDbId; the two are different columns.
   */
  orderPublicId?: string

  /** One page, oldest-bounded by `cursor` (null for the newest page). */
  fetchPage: (orderDbId: string | number, cursor: number | null) => Promise<{ data: any }>
  /** Everything newer than `afterId`. Used only for reconnect gap recovery. */
  fetchAfter: (orderDbId: string | number, afterId: number) => Promise<{ data: any }>

  /** Subscribe to incoming messages. Returns its own unsubscribe. */
  listenForMessages: (cb: (data: unknown) => void) => () => void
  /** Emit a message, with an optional acknowledgement callback. */
  sendMessage: (payload: MessageType, ack?: (result: SendAckResult) => void) => void
  /**
   * Tells the server this order's chat is the one on screen, so it can skip
   * pushing a chat notification to this viewer while they're already looking
   * at it. Optional: a caller that omits it simply never suppresses (the
   * receiver keeps getting notified even while the chat is open).
   */
  setActiveOrderChat?: (orderId: string | null) => void
  /**
   * Subscribe to system notifications. Optional: a caller that omits it simply
   * never refreshes per-message read ticks live (they still catch up on the
   * next natural refetch, e.g. reopening the thread).
   */
  recieveNotification?: (cb: (data: unknown) => void) => () => void
  /** The viewer's own role, to tell an opposite-side read from same-side noise. */
  viewerRole?: string
  /** Bumps on every socket RE-connect, signalling that a gap must be recovered. */
  reconnectNonce: number
  /**
   * Live socket connection state. Optional: a caller that does not pass it is
   * simply never reported as offline, which is how this behaved before.
   */
  isConnected?: boolean

  /** Invalidated after a send so the chat list preview updates. */
  sidebarQueryKey?: readonly unknown[]
}

export interface UseOrderChatResult {
  /** The list to render: confirmed messages plus sends still in flight. */
  messages: MessageType[]
  /** Pass to ChatSection's onSendMessage. */
  handleSend: (payload: MessageType) => void
  /** Pass to ChatSection's onRetryMessage. */
  handleRetry: (payload: MessageType) => void
  /**
   * The socket has been down long enough to be worth telling the user about.
   * Pass to ChatSection's isOffline.
   */
  isOffline: boolean
  chatQueryKey: readonly unknown[]
  hasNextPage: boolean
  fetchNextPage: () => void
  isFetchingNextPage: boolean
  isLoading: boolean
}

/** Stable identity, so a guarded fallback cannot retrigger memos. */
const EMPTY_MESSAGES: MessageType[] = []

/**
 * Module-level so the default is referentially stable. A literal in the
 * parameter list would be a fresh array on every render, which would make
 * handleSend a new function every render and re-subscribe everything
 * downstream of it.
 */
const DEFAULT_SIDEBAR_QUERY_KEY: readonly unknown[] = ["all-customer-order-chats"]

/**
 * How long the socket must stay down before the UI says so.
 *
 * The socket is legitimately disconnected for a moment on every page load and
 * through every ordinary reconnect. Reporting those would put a warning on
 * screen during normal operation, which teaches the user to ignore it -- and
 * the one time it matters is exactly when it must not be ignored.
 */
const OFFLINE_GRACE_MS = 3000

export function useOrderChat({
  orderDbId,
  orderPublicId,
  fetchPage,
  fetchAfter,
  listenForMessages,
  sendMessage,
  setActiveOrderChat,
  recieveNotification,
  viewerRole,
  reconnectNonce,
  isConnected,
  sidebarQueryKey = DEFAULT_SIDEBAR_QUERY_KEY,
}: UseOrderChatParams): UseOrderChatResult {
  const queryClient = useQueryClient()

  /**
   * Sends this client has made that the server has not acknowledged yet. This
   * is the only state the server does not already hold, which is why a failed
   * send must keep it in order to offer a retry.
   */
  const [pendingMessages, setPendingMessages] = useState<MessageType[]>([])

  /**
   * Messages the server confirmed over the socket during this session.
   *
   * Deliberately NOT written into the paginated query cache: page 0 means "the
   * newest N", so appending to it grows it past the server's page size and the
   * next refetch snaps it back, silently orphaning whatever was pushed out of
   * the window.
   */
  const [liveMessages, setLiveMessages] = useState<MessageType[]>([])

  // Declared before the query that uses it: the cache writes below target this
  // exact key, so the two must never be spelled differently.
  const chatQueryKey = useMemo(() => ["order-chats", orderDbId] as const, [orderDbId])

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery({
    queryKey: chatQueryKey,
    queryFn: async ({ pageParam }) => await fetchPage(orderDbId!, pageParam as number | null),
    initialPageParam: null as number | null,
    // nextCursor is null once the oldest message has been reached, and
    // undefined is how react-query is told there are no further pages.
    getNextPageParam: (lastPage: any) => lastPage.data.pagination.nextCursor ?? undefined,
    enabled: !!orderDbId,
  })

  const pagedMessages = useMemo(() => {
    return data?.pages
      ? [...data.pages].reverse().flatMap((page: any) => page.data.orderChat)
      : []
  }, [data?.pages])

  /**
   * Everything the paginated query has EVER shown, accumulated.
   *
   * data.pages is a moving window, not a growing list: page 0 means "the newest
   * N", so once new messages exist a refetch returns a different N and the
   * displaced rows fall out of the cache while page 1's cursor stays frozen.
   * Those messages are still in the database but unreachable from the cache --
   * reproduced against real data, and it is what made a message disappear
   * mid-conversation. Accumulating makes the list monotonic.
   *
   * Keyed by order id in the SAME state value, not reset by a separate effect:
   * effects run in declaration order, so a separate reset could fire after the
   * accumulate effect in the commit where the order changes -- wiping a
   * conversation that had just been populated from cache, with nothing left to
   * trigger a re-accumulate.
   */
  const [seen, setSeen] = useState<{ orderId: unknown; messages: MessageType[] }>({
    orderId: orderDbId,
    messages: [],
  })

  useEffect(() => {
    setSeen(prev => {
      if (prev.orderId !== orderDbId) {
        return { orderId: orderDbId, messages: upsertMessages([], pagedMessages) }
      }
      if (!pagedMessages.length) return prev
      const next = upsertMessages(prev.messages, pagedMessages)
      return next === prev.messages ? prev : { orderId: prev.orderId, messages: next }
    })
  }, [pagedMessages, orderDbId])

  const seenMessages = seen.orderId === orderDbId ? seen.messages : EMPTY_MESSAGES

  const messages = useMemo(
    () => mergeChatMessages(seenMessages, liveMessages, pendingMessages),
    [seenMessages, liveMessages, pendingMessages]
  )

  // Mirrors `messages` so the reconnect effect can read the newest id without
  // taking a dependency on it and re-running on every incoming message.
  const messagesRef = useRef<MessageType[]>([])
  useEffect(() => {
    messagesRef.current = messages
  }, [messages])

  /**
   * Record a message the server has confirmed.
   *
   * Idempotent: the acknowledgement and the socket echo both deliver the same
   * message, in either order, and upsertMessage matches on id or client_msg_id
   * so it lands exactly once.
   */
  const acceptConfirmed = useCallback((message: MessageType) => {
    setLiveMessages(prev => upsertMessage(prev, message))
  }, [])

  // A different order is a different conversation. `seen` is keyed inline
  // above; these two only ever receive messages for the open order, so clearing
  // them here is safe regardless of effect ordering.
  useEffect(() => {
    setLiveMessages([])
    setPendingMessages([])
  }, [orderDbId])

  // Marks this order's chat as the one on screen for as long as this hook
  // instance is mounted with it. <Chat> has no key, so switching orders (in
  // AllChat, or via arrow-key navigation in OrderDetail) re-runs this rather
  // than mounting a fresh instance -- the cleanup below clears the previous
  // order before the new one is set, so exactly one order is ever marked
  // active per socket.
  useEffect(() => {
    if (!setActiveOrderChat || !orderPublicId) return
    setActiveOrderChat(orderPublicId)
    return () => {
      setActiveOrderChat(null)
    }
  }, [setActiveOrderChat, orderPublicId])

  useEffect(() => {
    const unsubscribe = listenForMessages((incoming: unknown) => {
      const newMessage = incoming as MessageType
      // Only accept messages for the chat that is open.
      if (String(newMessage.orderId) !== String(orderPublicId)) return

      // The server echoes to the sender too, so this fires for our own messages
      // as well. Reconciled by id / client_msg_id, so an echo that arrives
      // before or after the acknowledgement lands exactly once.
      acceptConfirmed(newMessage)
      if (newMessage.client_msg_id) {
        setPendingMessages(prev =>
          prev.filter(p => p.client_msg_id !== newMessage.client_msg_id)
        )
      }
    })
    return () => {
      unsubscribe()
    }
  }, [listenForMessages, orderPublicId, acceptConfirmed])

  // Per-message read ticks (ChatSection renders Check/CheckCheck per sent
  // message from `is_read`). That field is computed server-side per viewer
  // (see applyReadState, apps/api/src/orderChat/chatService.js) and is only
  // ever fresh as of the fetch that produced it -- a message already in
  // `messages` never updates its own `is_read` in place. A 'chat_read' event
  // from the opposite side means the paginated cache is stale for every
  // message this viewer sent, so refetch it; upsertMessage (chatMessages.ts)
  // then replaces each stale row wholesale with its corrected copy.
  useEffect(() => {
    if (!recieveNotification || !orderDbId) return
    const unsubscribe = recieveNotification((data: unknown) => {
      const notif = data as { action_type?: string; action_by?: string; order_id?: string | number }
      if (notif.action_type !== 'chat_read') return
      if (String(notif.order_id) !== String(orderDbId)) return
      if (!isOppositeSide(viewerRole, notif.action_by)) return
      queryClient.invalidateQueries({ queryKey: chatQueryKey })
    })
    return () => {
      unsubscribe()
    }
  }, [recieveNotification, orderDbId, viewerRole, chatQueryKey, queryClient])

  // The order on screen *now*. An acknowledgement can land after the user has
  // switched orders; liveMessages/pendingMessages then belong to the new order,
  // so a late ack for the old one must be dropped, not shown in the wrong chat.
  const currentOrderRef = useRef(orderPublicId)
  currentOrderRef.current = orderPublicId

  const handleSend = useCallback(
    (payload: MessageType) => {
      // Show it immediately, marked as in flight.
      setPendingMessages(prev => [...upsertMessage(prev, { ...payload, status: "sending" })])

      sendMessage(payload, result => {
        if (String(payload.orderId) !== String(currentOrderRef.current)) return
        if (result?.ok && result.message) {
          // Confirmed: the stored row replaces the optimistic copy.
          acceptConfirmed(result.message)
          setPendingMessages(prev =>
            prev.filter(p => p.client_msg_id !== payload.client_msg_id)
          )
        } else {
          // Failed or timed out. Keep it visible and marked, so the user can
          // retry -- rather than silently showing a message that was never
          // stored.
          //
          // The code is recorded, not discarded. Every failure used to render
          // an identical "Not sent" with nothing logged, which made a dead
          // socket (DISCONNECTED / TIMEOUT, retrying is futile until it is
          // back) indistinguishable from a rejected send (NOT_AUTHORIZED,
          // retrying will never help) or a transient server error
          // (SEND_FAILED, retrying is exactly right). Without that distinction
          // retry looks broken whenever the transport is the problem.
          const code = result?.code ?? "UNKNOWN"
          console.error(
            "[chat] send failed:",
            code,
            "client_msg_id:",
            payload.client_msg_id
          )

          // A client_msg_id collision can never resolve by retrying with the
          // same id: the conflicting row belongs to another conversation and is
          // not this message. Re-stamp it so the retry can actually succeed.
          const needsNewId = code === "CLIENT_MSG_ID_CONFLICT"
          setPendingMessages(prev =>
            prev.map(p =>
              p.client_msg_id === payload.client_msg_id
                ? {
                    ...p,
                    status: "failed" as const,
                    error_code: code,
                    ...(needsNewId ? { client_msg_id: newClientMessageId() } : {}),
                  }
                : p
            )
          )
        }
      })

      queryClient.invalidateQueries({ queryKey: sidebarQueryKey })
    },
    [sendMessage, acceptConfirmed, queryClient, sidebarQueryKey]
  )

  // Retry re-uses the original client_msg_id, so if the first attempt did reach
  // the database the server returns that same row instead of storing a second
  // copy (the UNIQUE index on client_msg_id makes this safe).
  const handleRetry = useCallback(
    (payload: MessageType) => {
      // `status` and `error_code` are client-only bookkeeping from the failed
      // attempt; do not put them on the wire, and do not let a stale code
      // linger on the retried copy.
      const clean: MessageType = { ...payload }
      delete (clean as Partial<MessageType>).status
      delete (clean as Partial<MessageType>).error_code
      handleSend(clean)
    },
    [handleSend]
  )

  // ---------------------------------------------------------------------
  // Gap recovery after a dropped socket.
  // ---------------------------------------------------------------------
  // While the socket is down no live messages arrive, and reconnecting does not
  // replay them -- so anything sent during the gap stayed invisible until a
  // full page reload. The socket store re-joins the rooms; this fetches
  // whatever was missed and folds it in.
  useEffect(() => {
    if (!reconnectNonce || !orderDbId) return
    let cancelled = false

    ;(async () => {
      // Read from the rendered list, not the query cache: socket messages are
      // held outside the cache, so the cache's newest id can lag well behind
      // what the user has actually seen and we would re-request messages we
      // already have.
      const newestId = messagesRef.current.reduce(
        (max, m) => (m.id != null && Number(m.id) > max ? Number(m.id) : max),
        0
      )

      if (!newestId) {
        // Nothing loaded to anchor an `after` query -- an empty chat, or one
        // whose first page has not arrived. Everything sent during the gap
        // would stay invisible, so refetch instead of giving up.
        queryClient.invalidateQueries({ queryKey: chatQueryKey })
        return
      }

      const res = await fetchAfter(orderDbId, newestId)
      if (cancelled) return

      // A gap larger than one batch is not worth stitching -- refetch the
      // conversation instead, so ordering and paging stay consistent.
      if (res.data?.pagination?.hasMore) {
        queryClient.invalidateQueries({ queryKey: chatQueryKey })
        return
      }
      ;(res.data?.orderChat ?? []).forEach((m: MessageType) => acceptConfirmed(m))
    })().catch(console.error)

    return () => {
      cancelled = true
    }
  }, [reconnectNonce, orderDbId, chatQueryKey, queryClient, acceptConfirmed, fetchAfter])

  // ---------------------------------------------------------------------
  // Offline reporting.
  // ---------------------------------------------------------------------
  // Held behind a grace period rather than mirroring `isConnected` directly,
  // so the brief gap on load and on every ordinary reconnect stays invisible.
  const [isOffline, setIsOffline] = useState(false)

  useEffect(() => {
    if (isConnected !== false) {
      setIsOffline(false)
      return
    }
    const timer = setTimeout(() => setIsOffline(true), OFFLINE_GRACE_MS)
    return () => clearTimeout(timer)
  }, [isConnected])

  return {
    messages,
    handleSend,
    handleRetry,
    isOffline,
    chatQueryKey,
    hasNextPage: !!hasNextPage,
    fetchNextPage,
    isFetchingNextPage: !!isFetchingNextPage,
    isLoading,
  }
}
